import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Card } from '../components/Card';
import { Rejection } from '../components/Rejection';
import { PageHeader } from '../components/Shell';
import { Waiting } from '../components/Waiting';
import { ApiError } from '../net/client';
import { listAllProducts } from '../net/products';
import {
  canFlag,
  categoryLabels,
  codeStatusLabels,
  codeTail,
  detailWhen,
  exportFileName,
  exportReports,
  flagCode,
  getPhoto,
  inFilter,
  listAllReports,
  nextStatuses,
  rowWhen,
  setReportStatus,
  severeCategories,
  type ProductReport,
  type ReportPhoto,
  type ReportStatus,
  type StatusFilter,
  type TargetStatus,
} from '../net/productReports';
import { useOpenReports } from '../session/openReports';
import { useSession } from '../session/useSession';
import { chip, colors, fonts, radius, shell, type, verdict } from '../theme';
import { saveFile } from './saveFile';

/**
 * G7, `design/PortalReportsInbox.dc.html`.
 *
 * Every page of `GET /v1/product-reports` and every product, once; the four
 * pills filter locally. The detail moves a report on through
 * `PATCH /v1/product-reports/:id/status`, and a refusal, a member who is not
 * an admin included, is the rejection box under the buttons. Photos are
 * fetched as bytes and shown from a `blob:` URL, so the signed URL never
 * reaches the page. What the board does not draw is D13.
 *
 * Export CSV saves `GET /v1/product-reports/export` whole as the API names
 * it. Flag this unit asks first, in the button row, then
 * `PATCH /v1/codes/:codeId/status` with `flagged`; after it the detail's Code
 * fact and the row say so from the report's `codeStatus`, and the button is
 * gone. Neither state is drawn: D38.
 */

type Load =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | { status: 'loaded'; reports: ProductReport[]; names: Map<string, string> };

const filters: { key: StatusFilter; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'reviewing', label: 'Reviewing' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
];

const statusLabels: Record<ReportStatus, string> = {
  open: 'Open',
  reviewing: 'Reviewing',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

/** Open, Reviewing and Resolved are drawn; Dismissed takes the quiet chip, D13. */
const statusTones: Record<ReportStatus, { bg: string; fg: string }> = {
  open: chip.neutral,
  reviewing: chip.brand,
  resolved: { bg: verdict.genuine.bg, fg: verdict.genuine.fg },
  dismissed: chip.muted,
};

const actionLabels: Record<TargetStatus, string> = {
  reviewing: 'Start review',
  resolved: 'Resolve',
  dismissed: 'Dismiss',
};

export function ConsumerReports() {
  const { api } = useSession();
  const openReports = useOpenReports();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [filter, setFilter] = useState<StatusFilter>('open');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportRefusal, setExportRefusal] = useState<string | null>(null);
  const { set: setOpenCount } = openReports;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    Promise.all([listAllReports(api, signal), listAllProducts(api, signal)]).then(
      ([inbox, products]) => {
        setLoad({
          status: 'loaded',
          reports: inbox.reports,
          names: new Map(products.map((p) => [p.id, p.name])),
        });
        setOpenCount(inbox.openCount);
      },
      (error: unknown) => {
        if (!signal.aborted) setLoad({ status: 'failed', error });
      },
    );
    return () => controller.abort();
  }, [api, setOpenCount]);

  const reports = load.status === 'loaded' ? load.reports : [];
  const shown = reports.filter((r) => inFilter(r.status, filter));
  // The selection outlives its filter: a report just moved out of Open stays
  // open in the detail, and the next pill press picks afresh.
  const selected = reports.find((r) => r.id === selectedId) ?? shown[0];

  const pick = (next: StatusFilter) => {
    setFilter(next);
    setSelectedId(reports.find((r) => inFilter(r.status, next))?.id ?? null);
  };

  const replace = (before: ProductReport, after: ProductReport) => {
    if (load.status !== 'loaded') return;
    setLoad({ ...load, reports: load.reports.map((r) => (r.id === after.id ? after : r)) });
    setSelectedId(after.id);
    if (before.status === 'open' && after.status !== 'open' && openReports.count !== null) {
      setOpenCount(Math.max(0, openReports.count - 1));
    }
  };

  const download = async () => {
    setExporting(true);
    setExportRefusal(null);
    try {
      saveFile(await exportReports(api), exportFileName(new Date()));
    } catch (error) {
      setExportRefusal(failureText(error));
    }
    setExporting(false);
  };

  const now = new Date();
  const nameOf = (report: ProductReport) =>
    (report.productId !== undefined && load.status === 'loaded'
      ? load.names.get(report.productId)
      : undefined) ?? codeTail(report.codeId);

  return (
    <>
      {load.status === 'loading' ? <Waiting>Loading consumer reports</Waiting> : null}
      <PageHeader
        title="Consumer reports"
        {...(openReports.count !== null
          ? { subtitle: `${openReports.count} open · filed by people who scanned your products` }
          : {})}
        action={
          <button
            className="vp-outline"
            type="button"
            disabled={exporting || load.status !== 'loaded'}
            aria-busy={exporting}
            onClick={() => void download()}
            style={outlineButton(colors.navy)}
          >
            Export CSV
          </button>
        }
      />
      {exportRefusal !== null ? <Rejection>{exportRefusal}</Rejection> : null}
      {load.status === 'failed' ? (
        <Card title="Reports could not be loaded" body={failureText(load.error)} />
      ) : (
        <div
          aria-busy={load.status === 'loading'}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.25fr',
            gap: 16,
            flexGrow: 1,
            alignItems: 'start',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div role="group" aria-label="Status" style={{ display: 'flex', gap: 8 }}>
              {filters.map((f) => (
                <button
                  className="vp-outline"
                  key={f.key}
                  type="button"
                  aria-pressed={filter === f.key}
                  onClick={() => pick(f.key)}
                  style={pillStyle(filter === f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {load.status === 'loaded' ? (
              <ul
                aria-label="Reports"
                style={{ ...panel, listStyle: 'none', margin: 0, padding: 0 }}
              >
                {shown.length === 0 ? (
                  <li style={{ ...type.caption, color: colors.mutedInk, padding: '13px 16px' }}>
                    No reports in this list.
                  </li>
                ) : (
                  shown.map((report, i) => (
                    <li key={report.id}>
                      {i > 0 ? <div style={{ height: 1, background: colors.line }} /> : null}
                      <Row
                        report={report}
                        product={nameOf(report)}
                        when={rowWhen(report.createdAt, now)}
                        selected={report.id === selected?.id}
                        onSelect={() => setSelectedId(report.id)}
                      />
                    </li>
                  ))
                )}
              </ul>
            ) : null}
            <div style={{ ...type.caption, color: colors.mutedInk, lineHeight: 1.5 }}>
              A reference repeats when the same person reports again.
            </div>
          </div>
          {selected ? (
            <Detail
              key={selected.id}
              report={selected}
              product={nameOf(selected)}
              when={detailWhen(selected.createdAt, now)}
              onMoved={replace}
            />
          ) : null}
        </div>
      )}
    </>
  );
}

function Row({
  report,
  product,
  when,
  selected,
  onSelect,
}: {
  report: ProductReport;
  product: string;
  when: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      className="vp-outline"
      type="button"
      aria-current={selected ? 'true' : undefined}
      onClick={onSelect}
      style={rowStyle(selected)}
    >
      <span style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ ...type.bodyStrong, color: colors.navy }}>
          {categoryLabels[report.category]}
        </span>
        <span style={{ ...type.caption, color: colors.mutedInk }}>
          {product} {'·'} {when}
          {report.codeStatus === 'flagged' || report.codeStatus === 'retired' ? (
            <span style={{ color: verdict.unknown.fg }}>
              {' · '}code {report.codeStatus}
            </span>
          ) : null}
        </span>
        <span style={{ fontFamily: fonts.mono, fontSize: '11px', color: colors.mutedInk }}>
          {report.reporterRef}
        </span>
      </span>
      <Chip tone={statusTones[report.status]}>{statusLabels[report.status]}</Chip>
    </button>
  );
}

function Detail({
  report,
  product,
  when,
  onMoved,
}: {
  report: ProductReport;
  product: string;
  when: string;
  onMoved: (before: ProductReport, after: ProductReport) => void;
}) {
  const { api } = useSession();
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [flagging, setFlagging] = useState<'idle' | 'confirming'>('idle');
  const moves = nextStatuses(report.status);
  const seen = verdict[report.scanVerdict];
  const code = codeTail(report.codeId);

  /** D38: the same PATCH shape as a status move, on the code instead of the report. */
  const flag = () => {
    setBusy(true);
    setRefusal(null);
    setFlagging('idle');
    flagCode(api, report).then(
      (after) => {
        setBusy(false);
        onMoved(report, after);
      },
      (error: unknown) => {
        setBusy(false);
        setRefusal(failureText(error));
      },
    );
  };

  const move = (status: TargetStatus) => {
    setBusy(true);
    setRefusal(null);
    setReportStatus(api, report, status).then(
      (after) => {
        setBusy(false);
        onMoved(report, after);
      },
      (error: unknown) => {
        setBusy(false);
        setRefusal(failureText(error));
      },
    );
  };

  return (
    <section aria-label="Report" style={panel}>
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Chip
              tone={
                severeCategories.has(report.category)
                  ? { bg: verdict.unknown.bg, fg: verdict.unknown.fg }
                  : chip.neutral
              }
            >
              {categoryLabels[report.category]}
            </Chip>
            <Chip tone={statusTones[report.status]}>{statusLabels[report.status]}</Chip>
          </div>
          <div style={{ ...type.meta, color: colors.mutedInk }}>{when}</div>
        </div>

        <h2
          style={{
            fontFamily: fonts.display,
            fontWeight: 800,
            fontSize: '21px',
            color: colors.navy,
            margin: 0,
          }}
        >
          {product}
        </h2>

        {report.note ? <blockquote style={quote}>"{report.note}"</blockquote> : null}

        {report.photos.length > 0 ? (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {report.photos.map((photo, i) => (
              <Photo
                key={photo.id}
                photo={photo}
                label={`Photo ${i + 1} of ${report.photos.length}`}
              />
            ))}
          </div>
        ) : null}

        <dl style={facts}>
          {report.sellerHint ? (
            <Fact label="Bought at">
              <span style={{ fontWeight: 600 }}>{report.sellerHint}</span>
            </Fact>
          ) : null}
          <Fact label="Verdict shown">
            <span style={{ fontWeight: 700, color: seen.fg }}>{seen.label}</span>
          </Fact>
          <Fact label="Code">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: fonts.mono }}>{code}</span>
              {report.codeStatus === 'flagged' || report.codeStatus === 'retired' ? (
                <Chip
                  tone={
                    report.codeStatus === 'flagged'
                      ? { bg: verdict.unknown.bg, fg: verdict.unknown.fg }
                      : chip.muted
                  }
                >
                  {codeStatusLabels[report.codeStatus]}
                </Chip>
              ) : null}
            </span>
          </Fact>
          <Fact label="Reporter">
            <span style={{ fontFamily: fonts.mono }}>{report.reporterRef}</span>
          </Fact>
          <Fact label="Contact">
            {report.contactConsent && report.reporterEmail ? (
              <span style={{ fontWeight: 600 }}>{report.reporterEmail}</span>
            ) : (
              <span style={{ fontWeight: 600, color: colors.mutedInk }}>Not shared</span>
            )}
          </Fact>
        </dl>

        {flagging === 'confirming' ? (
          <div
            role="group"
            aria-label="Flag this unit"
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <span style={{ ...type.bodyStrong, color: colors.navy }}>
              Flag code {code}? People who scanned it will be told.
            </span>
            <div style={{ display: 'flex', gap: 12 }}>
              <button className="vp-fill" type="button" onClick={flag} style={primaryButton}>
                Flag
              </button>
              <button
                className="vp-outline"
                type="button"
                onClick={() => setFlagging('idle')}
                style={outlineButton(colors.navy)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : moves.length > 0 ? (
          <div style={{ display: 'flex', gap: 12 }}>
            {moves
              .filter((s) => s !== 'dismissed')
              .map((s) => (
                <button
                  className="vp-fill"
                  key={s}
                  type="button"
                  disabled={busy}
                  onClick={() => move(s)}
                  style={primaryButton}
                >
                  {actionLabels[s]}
                </button>
              ))}
            {canFlag(report) ? (
              <button
                className="vp-outline"
                type="button"
                disabled={busy}
                onClick={() => setFlagging('confirming')}
                style={outlineButton(colors.navy)}
              >
                Flag this unit
              </button>
            ) : null}
            {moves.includes('dismissed') ? (
              <button
                className="vp-outline"
                type="button"
                disabled={busy}
                onClick={() => move('dismissed')}
                style={outlineButton(verdict.unknown.fg)}
              >
                {actionLabels.dismissed}
              </button>
            ) : null}
          </div>
        ) : null}
        {refusal !== null ? <Rejection>{refusal}</Rejection> : null}
        <div style={{ ...type.caption, color: colors.mutedInk }}>
          Reports move one way: open {'→'} reviewing {'→'} resolved or dismissed.
        </div>
      </div>
    </section>
  );
}

/**
 * One photo, fetched as bytes and shown from an object URL that is revoked on
 * unmount. No link, no signed URL: the page never holds anything to copy.
 */
function Photo({ photo, label }: { photo: ReportPhoto; label: string }) {
  const { api } = useSession();
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'failed' } | { status: 'loaded'; src: string }
  >({
    status: 'loading',
  });

  useEffect(() => {
    const controller = new AbortController();
    let src: string | null = null;
    getPhoto(api, photo, controller.signal).then(
      (blob) => {
        if (controller.signal.aborted) return;
        src = URL.createObjectURL(blob);
        setState({ status: 'loaded', src });
      },
      () => {
        if (!controller.signal.aborted) setState({ status: 'failed' });
      },
    );
    return () => {
      controller.abort();
      if (src !== null) URL.revokeObjectURL(src);
    };
    // Keyed on the URL, not the object: a status change hands back a new
    // report, and its photos are the same bytes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, photo.url]);

  if (state.status === 'loaded') {
    return <img src={state.src} alt={label} style={{ ...photoTile, objectFit: 'cover' }} />;
  }
  return (
    <div
      role="img"
      aria-label={state.status === 'failed' ? `${label} could not be loaded` : label}
      aria-busy={state.status === 'loading'}
      style={photoTile}
    >
      {state.status === 'failed' ? (
        <span
          style={{ ...type.captionSm, color: colors.mutedInk, padding: 8, textAlign: 'center' }}
        >
          Could not load
        </span>
      ) : null}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      style={{ display: 'flex', justifyContent: 'space-between', ...type.meta, color: colors.navy }}
    >
      <dt style={{ color: colors.mutedInk }}>{label}</dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </div>
  );
}

function Chip({ tone, children }: { tone: { bg: string; fg: string }; children: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        padding: '4px 12px',
        borderRadius: radius.chip,
        background: tone.bg,
        color: tone.fg,
        ...type.captionBold,
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );
}

function failureText(error: unknown): string {
  if (error instanceof ApiError && error.kind === 'network') {
    return 'The API could not be reached. Reload the page to try again.';
  }
  if (error instanceof Error) return error.message;
  return 'Reload the page to try again.';
}

const panel: CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.line}`,
  borderRadius: radius.card,
};

/** The board's pills: 10px by 16px, 13px, navy when selected. */
const pillStyle = (selected: boolean): CSSProperties => ({
  padding: '10px 16px',
  borderRadius: 20,
  cursor: 'pointer',
  ...(selected
    ? { background: colors.navy, color: colors.white, border: 'none', ...type.metaBold }
    : {
        background: colors.card,
        color: colors.mutedInk,
        border: `1px solid ${colors.line}`,
        ...type.metaStrong,
      }),
});

const rowStyle = (selected: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  padding: '13px 16px 13px 12px',
  border: 'none',
  borderLeft: `4px solid ${selected ? colors.brand : 'transparent'}`,
  background: selected ? colors.ground : 'transparent',
  textAlign: 'left',
  cursor: 'pointer',
});

const quote: CSSProperties = {
  borderLeft: `4px solid ${colors.line}`,
  padding: '2px 0 2px 14px',
  margin: 0,
  ...type.body,
  lineHeight: 1.6,
  color: colors.navy,
};

const facts: CSSProperties = {
  borderRadius: 12,
  background: colors.ground,
  border: `1px solid ${colors.line}`,
  padding: '16px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: 9,
  margin: 0,
};

/** Not drawn: 88px square, the facts box's radius and border, D13. */
const photoTile: CSSProperties = {
  width: 88,
  height: 88,
  boxSizing: 'border-box',
  borderRadius: 12,
  border: `1px solid ${colors.line}`,
  background: colors.ground,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const actionBase: CSSProperties = {
  height: shell.actionButton.height,
  padding: `0 ${shell.actionButton.paddingX}px`,
  borderRadius: shell.actionButton.radius,
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  ...type.button,
};

const primaryButton: CSSProperties = {
  ...actionBase,
  background: colors.brand,
  color: colors.white,
  border: 'none',
};

const outlineButton = (ink: string): CSSProperties => ({
  ...actionBase,
  background: colors.card,
  color: ink,
  border: `2px solid ${ink}`,
});
